import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function HarrowCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Harrow Pothole Claims | 7,055 Repairs in One Year | Fixtyer"
        description="Harrow Council filled 7,055 potholes in 2024/25 — eight times the 2020/21 figure — yet one in four unclassified roads remain in RED condition and a new AI survey shows wildly different results to prior inspections. See what the DfT AMBER rating means for your claim."
        canonical="/councils/harrow"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Harrow', url: '/councils/harrow' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Harrow spends twenty times its DfT capital allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect, not aggregate spend. Harrow is AMBER overall because road condition remains mixed — 24–26% of unclassified roads have been in RED condition at every comparable survey since 2021, and B/C roads sit at 25–28% RED. Section 58 requires the council to show it reasonably knew about and addressed the defect that damaged your vehicle.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 390km — 85% of Harrow\'s 457km carriageway network. At every survey year with published data (2021, 2023 and 2024), roughly one in four U-roads was in RED condition: 26%, 26% and 24% respectively. That is over 90km of residential streets in poor condition at the last count.',
          },
          {
            question: 'Does Harrow\'s new AI survey showing 94% green A-roads weaken my claim?',
            answer: 'It complicates the council\'s own narrative, not yours. Harrow trialled an AI-based survey in 2024 and the council itself states the data "shows a significant variance compared to previous Detailed Visual Inspection (DVI) survey results." Under DVI, A-roads were 57% green in 2024; under AI, 93.95% green. The two methodologies cannot be compared — and Harrow has commissioned both DVI and AI surveys for 2025/26 to identify the differences.',
          },
          {
            question: 'Does the jump to 7,055 pothole repairs in 2024/25 mean the roads are fixed?',
            answer: 'No. The repair surge reflects new Pothole Pro and thermal repair technology launched from May 2024, not a network cured of defects. Harrow still projects approximately 3,800 reactive defect repairs for 2025/26, and 24% of U-roads remained in RED condition at the 2024 survey. A network producing thousands of potholes per year is one where defects routinely form between inspections.',
          },
          {
            question: 'Does Harrow\'s admission about TfL funding and utility works help my claim?',
            answer: 'Potentially. Harrow acknowledges TfL funding for its Borough Principal Road Network "has significantly declined" to a competitive process capped at £200,000 per borough, forcing self-funding of key routes. It also lists "premature failures caused by utility works" among ongoing challenges. If your defect formed after streetworks or on a formerly TfL-funded route, that context strengthens arguments about known deterioration risks.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Harrow Council"
        councilSlug="harrow"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/harrow"
        headline="Harrow Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Harrow</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            7,055 Pothole Repairs in One Year — and a Quarter of Residential Roads Still RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Harrow filled <strong className="text-rose-400">7,055 potholes in 2024/25 alone</strong> — more than 
            eight times the 831 repaired in 2020/21 — after launching London's first Pothole Pro scheme. 
            The DfT rates spend <strong className="text-emerald-400">GREEN</strong>, yet the overall scorecard 
            is <strong className="text-amber-400">AMBER</strong> because <strong className="text-rose-400">24–26% 
            of unclassified roads</strong> have sat in RED condition at every comparable survey since 2021, 
            and a new AI inspection method produces figures the council admits vary "significantly" from prior results.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">11,609</div>
            <div className="text-white font-medium mt-2">Potholes filled across five years</div>
            <div className="text-brand-300 text-sm mt-1">
              From 831 in 2020/21 to 7,055 in 2024/25 — on a 457km network where 390km 
              (85%) is unclassified residential road.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Survey data from Harrow's own transparency report — calendar-year condition figures 
            with gaps in 2020 and 2022
          </p>

          {/* Network size */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">Harrow's network at a glance</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">43km</div>
                <div className="text-sm text-surface-600">A-roads (9%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">24km</div>
                <div className="text-sm text-surface-600">B and C roads (5%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">390km</div>
                <div className="text-sm text-rose-800">U-roads (85%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">457km</div>
                <div className="text-sm text-surface-600">Total carriageway</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Plus 935km of footways, 50.25km of public rights of way and 64km of cycleways. 
              The overwhelming majority of roads where pothole claims arise are unclassified residential streets.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (43km — 9% of network): mixed picture</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">49%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">53%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024 (DVI)</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">22%</td>
                    <td className="px-4 py-2 text-center font-bold">21%</td>
                    <td className="px-4 py-2 text-center font-bold">57%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono text-surface-500">2024 (AI survey)</td>
                    <td className="px-4 py-2 text-center text-surface-500">0.91%</td>
                    <td className="px-4 py-2 text-center text-surface-500">5.14%</td>
                    <td className="px-4 py-2 text-center text-surface-500">93.95%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Under the established DVI methodology, A-road RED condition has barely moved (23% → 22%) 
              while green roads improved from 49% to 57%. The AI survey paints a radically different picture — 
              covered in detail below.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (24km — 5% of network): persistently poor</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">28%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">47%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">27%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">53%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024 (DVI)</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">25%</td>
                    <td className="px-4 py-2 text-center font-bold">25%</td>
                    <td className="px-4 py-2 text-center font-bold">50%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              On just 24km of B and C road, roughly a quarter has been in RED condition at every survey — 
              and half the network needs maintenance or will soon. These are Harrow's Borough Principal Road Network routes, 
              formerly supported by TfL funding that the council says has "significantly declined."
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Heavy Spend, Stubborn Condition</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£782k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£16.1m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">97%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Harrow projects spending over <span className="text-emerald-400">twenty times its DfT allocation</span> on 
              highway maintenance in 2025/26 — with 97% classed as preventative. The DfT Spend scorecard is GREEN. 
              Yet one in four residential roads remains in RED condition, and the overall rating is still AMBER.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 390km Residential Network
          </h2>
          <p className="text-surface-600 mb-8">
            85% of Harrow's carriageway is unclassified — where most pothole damage claims originate
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">No survey (COVID)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Data unavailable</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">24% <span className="text-surface-400 text-xs font-normal">(1.32% AI survey)</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~94km of Residential Road in Poor Condition</h3>
              <p className="text-surface-600 text-sm mb-3">
                At 24–26% RED across three comparable survey years, roughly 94–101km of Harrow's 
                390km unclassified network requires structural maintenance. That is the equivalent of 
                every residential street in a medium-sized town — in persistent poor condition.
              </p>
              <p className="text-surface-600 text-sm">
                Harrow states it "conducts an annual condition survey covering the entire highway network," 
                yet published U-road data exists only for 2021, 2023 and 2024 — with gaps in 2020 and 2022.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Inspection Regime</h3>
              <p className="text-amber-800 text-sm mb-3">
                Harrow's HIAMP states roads and footways are inspected 2–3 times annually, with 
                public reports encouraged. For 2025/26, the council projects reactive repair of 
                approximately 3,800 defects — subject to weather and network deterioration.
              </p>
              <p className="text-amber-800 text-sm">
                The gap between 3,800 projected reactive repairs and 7,055 potholes actually filled 
                in 2024/25 tells you how many defects the network produces beyond the planned programme.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 58 of the Highways Act 1980, Harrow must show it had a reasonable system 
              for inspecting and maintaining the specific road where your damage occurred. For residential streets, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road among the 24–26% in RED condition at the last survey?</li>
              <li>• When was it last condition-surveyed — 2021, 2023 or 2024?</li>
              <li>• Did the 2–3 annual safety inspections catch the defect before it damaged your vehicle?</li>
              <li>• Were there prior reports via Harrow's online reporting system?</li>
            </ul>
            <p className="text-white font-medium">
              A quarter of the residential network in poor condition is not a snapshot of neglect — 
              it is a pattern repeated across three survey years.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Surge */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            11,609 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The 2024/25 repair surge followed Pothole Pro and thermal technology — not a cured network
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">831</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">851</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,150</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">1,722</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">7,055</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11,609</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="LB Harrow launched the 'Pothole Pro' project in May 2024. This initiative introduced advanced road repair machinery, making Harrow the first London borough to implement this technology. The project targets roads not included in the annual surfacing programme, but which require targeted repairs to prevent further deterioration of the network and to extend the lifespan of highway assets."
              source="Harrow Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The increased figure for 2024 reflects the Council's proactive and reactive approach to defect repairs, aimed at improving road safety and the overall condition of the network."
              source="Harrow Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~19 Repairs Every Day in 2024/25</h3>
              <p className="text-surface-600 text-sm">
                Seven thousand pothole repairs in a single year on 457km of road means defects are forming 
                continuously — not occasionally. A network producing potholes at that rate is one where 
                the gap between inspections and new defects is exactly what prior reports and photos must prove.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 Forward Plan</h3>
              <p className="text-amber-800 text-sm">
                Harrow plans 65 carriageway resurfacing schemes (~15.4km), 38 footway renewals (~20km), 
                Pothole Pro/thermal treatment across ~150 locations (~30,000 sq m), and approximately 
                3,800 reactive defect repairs. That is proactive — but it confirms thousands of defects 
                are still expected annually.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The AI Survey Asterisk */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The AI Survey That Changed the Numbers
          </h2>
          <p className="text-surface-600 mb-8">
            Harrow's own admission that 2024 AI results vary "significantly" from prior inspections
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="In 2024, Harrow trialled an AI-based survey method. As a result, the data submitted shows a significant variance compared to previous Detailed Visual Inspection (DVI) survey results. For the 2025/26 financial year, Harrow has commissioned both a DVI survey and an AI survey. Comparing these datasets will enable the team to identify methodological differences, validate the data, and support more informed decision-making."
              source="Harrow Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">DVI RED (2024)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">AI GREEN (2024)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-medium">A-roads</td>
                  <td className="px-4 py-3 text-center">22% RED / 57% green</td>
                  <td className="px-4 py-3 text-center">0.91% RED / 93.95% green</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-medium">B and C roads</td>
                  <td className="px-4 py-3 text-center">25% RED / 50% green</td>
                  <td className="px-4 py-3 text-center">1.24% RED / 93.01% green</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">U-roads</td>
                  <td className="px-4 py-3 text-center">24% RED</td>
                  <td className="px-4 py-3 text-center">1.32% RED</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                The same network, surveyed two ways in the same year, produces RED rates of 22–25% under DVI 
                and under 1.5% under AI. Harrow acknowledges this variance and is running both methods in 
                2025/26 to understand the gap.
              </p>
              <p className="text-surface-600 text-sm">
                For your claim, the DVI figures are the established baseline. If Harrow cites AI survey 
                results in a Section 58 defence, the council's own report documents why those figures 
                are not directly comparable.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Which survey methodology was used for the road where your damage occurred?</li>
                <li>• Was your road in the 22–25% DVI RED category at the 2024 survey?</li>
                <li>• Can the council produce DVI condition data, not just AI results, for your street?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* TfL and Utility Admissions */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            TfL Funding Collapse and Utility Damage
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own explanation for pressure on principal routes — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Funding from TfL—which traditionally supported the Borough Principal Road Network (BPRN)—has significantly declined. It is now limited to a competitive bidding process, with only a few boroughs selected and funding capped at £200,000 per borough. Given that these roads form a vital part of London's transport infrastructure and require higher resurfacing costs due to their specifications and multiple lanes, maintaining them has been challenging. Despite this, we have managed to uphold their condition effectively. In recent years, the borough has had no choice but to self-fund the maintenance of these key routes."
              source="Harrow Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Ongoing Challenges Listed</h3>
              <p className="text-surface-600 text-sm mb-3">
                Harrow's transparency report identifies structural pressures beyond budget:
              </p>
              <ul className="text-surface-600 text-sm space-y-1">
                <li>• Limited funding resources</li>
                <li>• Competitive TfL A-road bidding capped at £200k</li>
                <li>• Increasing traffic volumes and heavier vehicles (e.g. electric vehicles)</li>
                <li>• Weather-related deterioration</li>
                <li>• Premature failures caused by utility works</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Section 58 on Principal Routes</h3>
              <p className="text-amber-800 text-sm">
                Harrow issues Section 58 notices to utility companies before resurfacing works — 
                coordinating streetworks to minimise premature surface failures. If your pothole formed 
                after utility excavation on or near a BPRN route, the council's own admission of 
                "premature failures caused by utility works" is relevant context for whether the 
                defect should have been identified and repaired sooner.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a High-Spending AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Harrow invests heavily — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — projects £16.1m capital against £782k DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 97% preventative spend projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Pothole Pro innovation — first London borough to deploy the technology
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented HIAMP with 2–3 annual inspections and online reporting
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road green condition improving under DVI (49% → 57%)
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-resourced Section 58 defence. Generic "the roads are bad" claims will not succeed.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 24–26% of U-roads RED at every comparable survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C roads persistently 25–28% RED on the BPRN
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 11,609 potholes filled in five years — defects outpace inspection cycles
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AI survey variance — council admits DVI and AI results differ "significantly"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> TfL funding collapse and utility-work premature failures documented
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Condition data gaps in 2020 and 2022 despite claimed annual surveys
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and Pothole Pro investment, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, Harrow's online reporting system) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, cite the persistent 24–26% RED condition rate</li>
              <li>• Whether utility works preceded the defect — Harrow lists this as a known failure mode</li>
              <li>• Request DVI survey data for your street, not AI figures the council itself says vary significantly</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Harrow's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Harrow?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A high-spending council demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road RED-rate argument</li>
                <li>• No prior-report search</li>
                <li>• No DVI vs AI survey challenge</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 24–26% U-road RED condition documented</li>
                <li>✅ 11,609 pothole repairs in five years cited</li>
                <li>✅ AI survey variance argued from council's own report</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Harrow</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=harrow"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Harrow Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Harrow spends twenty times its DfT capital allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect, not aggregate spend. 
                Harrow is AMBER overall because road condition remains mixed — 24–26% of unclassified roads have been in 
                RED condition at every comparable survey since 2021, and B/C roads sit at 25–28% RED. Section 58 requires 
                the council to show it reasonably knew about and addressed the defect that damaged your vehicle.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 390km — 85% of Harrow's 457km carriageway network. At every survey year with 
                published data (2021, 2023 and 2024), roughly one in four U-roads was in RED condition: 26%, 26% and 24% 
                respectively. That is over 90km of residential streets in poor condition at the last count.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Harrow's new AI survey showing 94% green A-roads weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It complicates the council's own narrative, not yours. Harrow trialled an AI-based survey in 2024 and the 
                council itself states the data "shows a significant variance compared to previous Detailed Visual Inspection 
                (DVI) survey results." Under DVI, A-roads were 57% green in 2024; under AI, 93.95% green. The two 
                methodologies cannot be compared — and Harrow has commissioned both DVI and AI surveys for 2025/26 to 
                identify the differences.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the jump to 7,055 pothole repairs in 2024/25 mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The repair surge reflects new Pothole Pro and thermal repair technology launched from May 2024, not a 
                network cured of defects. Harrow still projects approximately 3,800 reactive defect repairs for 2025/26, and 
                24% of U-roads remained in RED condition at the 2024 survey. A network producing thousands of potholes per 
                year is one where defects routinely form between inspections.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Harrow's admission about TfL funding and utility works help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially. Harrow acknowledges TfL funding for its Borough Principal Road Network "has significantly 
                declined" to a competitive process capped at £200,000 per borough, forcing self-funding of key routes. It also 
                lists "premature failures caused by utility works" among ongoing challenges. If your defect formed after 
                streetworks or on a formerly TfL-funded route, that context strengthens arguments about known deterioration risks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://www.harrow.gov.uk/downloads/file/33211/Annex_A__Transparency_report_LB_Harrow.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Harrow Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
